import { PathMatch, useMatch } from "react-router-dom";
import routes from "./routes";

const ORDERS_PREFIX = `${routes.orders}/`;

/**
 * The literal segments that live under `/orders` and are therefore NOT booking
 * ids, even though `/orders/:orderId` happily captures them.
 *
 * Derived from the route constants so a new literal `/orders/...` route cannot
 * leave the guard behind.
 */
const reservedOrderSegments = [
  routes.acceptOrderPath.slice(ORDERS_PREFIX.length),
];

export const isReservedOrderSegment = (
  orderId: string | undefined
): boolean => orderId !== undefined && reservedOrderSegments.includes(orderId);

/**
 * `/orders/:orderId` as a STANDALONE matcher — the single place that answers
 * "is the current URL a booking detail URL, and which booking?".
 *
 * #2352: `useMatch(routes.orderDetailsPath)` on its own also matches
 * `/orders/accept`, the postpay acceptance deep link from the offer mail.
 * React Router only ranks the literal `accept` above `:orderId` when the two
 * compete as RENDERED routes; a standalone matcher has nothing to lose
 * against, so it handed back `orderId === "accept"`. That travelled into
 * `UseOrderDialog` -> `useFetchOrder("accept")` -> `GET /orders/accept`, which
 * 500s, and the error path redirects to the orders list — so no customer could
 * accept a postpay contract.
 *
 * Every consumer of a captured `orderId` goes through this hook rather than
 * calling `useMatch` on the pattern directly, so the guard cannot be forgotten
 * by the next one.
 */
export const useOrderDetailsMatch = (): PathMatch<"orderId"> | null => {
  const match = useMatch(routes.orderDetailsPath);
  return isReservedOrderSegment(match?.params.orderId) ? null : match;
};
